'use client'

import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import moment from 'moment-timezone'
import { cn } from '@/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
]

const COMMON_VALUES = new Set(COMMON_TIMEZONES.map((c) => c.value))
const ALL_TIMEZONES = moment.tz.names().filter((tz) => !COMMON_VALUES.has(tz))

function getLabel(tz: string): string {
  const common = COMMON_TIMEZONES.find((c) => c.value === tz)
  return common ? common.label : tz
}

interface TimezoneComboboxProps {
  value: string
  onChange: (tz: string) => void
}

export function TimezoneCombobox({ value, onChange }: TimezoneComboboxProps) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          aria-controls="timezone-listbox"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-sky-300 rounded-md bg-white hover:bg-sky-50 text-sky-900"
        >
          {getLabel(value)}
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone..." />
          <CommandList id="timezone-listbox" className="max-h-64 overflow-y-auto">
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup heading="Common">
              {COMMON_TIMEZONES.map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onChange(tz.value)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === tz.value ? 'opacity-100' : 'opacity-0')}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="All Timezones">
              {ALL_TIMEZONES.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={() => {
                    onChange(tz)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === tz ? 'opacity-100' : 'opacity-0')}
                  />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
